import { createAsyncThunk, createAction } from "@reduxjs/toolkit";
import { type AxiosResponse } from "axios";
import { httpRequest, type HttpRequestConfig } from "./httpRequest";

export interface ConnectPayload {
  instanceId?: string;
  queryParams?: Record<string, unknown>;
  id?: string;
  body?: Record<string, unknown>;
}

type RejectFn = (value: string | unknown) => unknown;

/**
 * HTTP GET request
 */
const retrieveData = async (
  payload: ConnectPayload,
  rejectWithValue: RejectFn,
): Promise<AxiosResponse | unknown> => {
  const { instanceId, queryParams } = payload;

  try {
    const requestConfig: HttpRequestConfig = {
      pathname: `/connectApis/${instanceId}`,
      method: "GET",
      queryParams,
    };
    return await httpRequest(requestConfig);
  } catch (err) {
    return rejectWithValue(`Error invoking connectApis. ${err}`);
  }
};

export const resetApiData = createAction<{ datasets?: string[] } | undefined>(
  "connect/reset",
);

export const retrieveApiData = createAsyncThunk<unknown, ConnectPayload>(
  "connect/retrieve",
  async (payload: ConnectPayload, { rejectWithValue }) => {
    return await retrieveData(payload, rejectWithValue);
  },
);

/**
 * HTTP DELETE request
 */
export const deleteApiData = createAsyncThunk<unknown, ConnectPayload>(
  "connect/delete",
  async (payload: ConnectPayload, { rejectWithValue }) => {
    const { instanceId, id, queryParams } = payload;
    const requestConfig: HttpRequestConfig = {
      pathname: `/connectApis/${instanceId}/${id}`,
      method: "DELETE",
      queryParams,
    };

    try {
      await httpRequest(requestConfig);

      return await retrieveData(payload, rejectWithValue);
    } catch (err) {
      return rejectWithValue(`Error invoking connectApis. ${err}`);
    }
  },
);

/**
 * Http POST request
 */
export const postApiData = createAsyncThunk<unknown, ConnectPayload>(
  "connect/post",
  async (payload: ConnectPayload, { rejectWithValue }) => {
    const { instanceId, id, queryParams, body } = payload;
    const requestConfig: HttpRequestConfig = {
      pathname: `/connectApis/${instanceId}/${id}`,
      method: "POST",
      queryParams,
      body,
    };

    try {
      return await httpRequest(requestConfig);
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);
